import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { LinkedRecordBar, PageHeader } from '@/components/core';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import { MY_TIME_OFF_TABS, MyTimeOffTab } from '@/constant/app-paths';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useLinkedRecord, useOpenLinkedRecord, useUrlTab } from '@/hooks/use-linked-record';
import { useServerTable } from '@/hooks/use-server-table';
import { mergeWhere } from '@/lib/table-query';
import { LeaveReviewDialog, LeaveSheetView } from '@/modules/time-off/team-time-off';
import { MyToilTab } from '@/modules/time-off/my-toil';
import { useTimeOffRequestActions } from '../../../shared/use-time-off-request-actions';
import { createLeaveHistoryTableColumns } from '../../components/leave-history-table/leave-history-table-columns';
import {
  useCurrentUserId,
  useGetLeaveRequests,
  useWithdrawLeaveRequest,
} from '../../hooks/use-leave';
import { DEFAULT_MY_LEAVE_REQUESTS_ORDER } from '../../graphql/queries';
import { LeaveRequest, LeaveRequestsPagingResult } from '../../types/leave.types';

const { HISTORY, ALLOCATION, TOIL } = MY_TIME_OFF_TABS;
/** Tab names of the former My Leave page, still in links already sent. */
const TAB_ALIASES: Record<string, MyTimeOffTab> = {
  requests: HISTORY,
  'leave-sheet': ALLOCATION,
  'leave-overview': ALLOCATION,
};

/** The signed-in user's leave requests, with withdraw; a notification's link opens its request. */
const LeaveHistoryTab = () => {
  const { t } = useTranslation();
  const userId = useCurrentUserId();
  const canWithdraw = useHasPermission(HRM_PERMISSIONS.TIME_OFF.LEAVE_WITHDRAW);
  const [withdrawTarget, setWithdrawTarget] = useState<LeaveRequest | null>(null);
  const [detailTarget, setDetailTarget] = useState<LeaveRequest | null>(null);

  // A notification's link lists only its request and opens it.
  const { linkedId, clearLinked } = useLinkedRecord();
  const { pagination, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_MY_LEAVE_REQUESTS_ORDER,
    linkedId,
  });

  const filter = { UserId: userId };
  const { data, isLoading, error } = useGetLeaveRequests({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filter: mergeWhere(filter, columnWhere),
    order,
  });
  const result = (data as { LeaveRequests?: LeaveRequestsPagingResult })?.LeaveRequests;
  const items = result?.items ?? [];
  const totalCount = result?.totalCount ?? 0;
  useOpenLinkedRecord(linkedId, items, setDetailTarget);

  const { mutateAsync: withdrawLeaveRequest, isPending: isWithdrawing } = useWithdrawLeaveRequest();

  // A refusal (not pending anymore, not the caller's own request) comes back as `IsSuccessful: false`
  // with its own message, toasted by `useWithdrawLeaveRequest`; the confirmation stays open then.
  const handleWithdrawConfirm = async () => {
    if (!withdrawTarget) return;
    const outcome = await withdrawLeaveRequest(withdrawTarget.ItemId);
    if (outcome.IsSuccessful) setWithdrawTarget(null);
  };

  const columns = createLeaveHistoryTableColumns({ t, onWithdraw: setWithdrawTarget, canWithdraw });

  return (
    <div className="flex flex-col gap-4">
      <LinkedRecordBar linkedId={linkedId} onShowAll={clearLinked} />
      <AdvanceDataTable
        {...tableProps}
        data={items}
        columns={columns}
        isLoading={isLoading}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount,
        }}
        isExpandRowContent={false}
        onRowClick={setDetailTarget}
        mobileView="cards"
      />

      {/* The review dialog offers no review/cancel/archive actions on the caller's own request. */}
      <LeaveReviewDialog
        open={Boolean(detailTarget)}
        onOpenChange={(open) => !open && setDetailTarget(null)}
        leaveRequest={detailTarget}
      />

      <ConfirmationModal
        open={Boolean(withdrawTarget)}
        onOpenChange={(open) => !open && setWithdrawTarget(null)}
        title={t('WITHDRAW_LEAVE_REQUEST')}
        description={t('ARE_YOU_SURE_WANT_TO_WITHDRAW')}
        confirmText={isWithdrawing ? t('WITHDRAWING') : t('WITHDRAW')}
        preventAutoClose={isWithdrawing}
        onConfirm={handleWithdrawConfirm}
      />
    </div>
  );
};

/**
 * My Time Off (HR Module Navigation Map): Leave History, Leave Sheet (the leave overview of the HR term
 * above its day-by-day ledger) and TOIL Requests (the former My TOIL page). Request Time Off and Request
 * TOIL are in the header. The tab lives in the URL (`?tab=`).
 */
export const MyTimeOffPage = () => {
  const { t } = useTranslation();
  const userId = useCurrentUserId();
  const canViewLeave = useHasPermission(HRM_PERMISSIONS.TIME_OFF.LEAVE_VIEW_OWN);
  const canViewSheet = useHasPermission(HRM_PERMISSIONS.TIME_OFF.LEAVE_VIEW_SHEET);
  const canViewOverview = useHasPermission(HRM_PERMISSIONS.TIME_OFF.LEAVE_VIEW_OVERVIEW);
  const canViewToil = useHasPermission(HRM_PERMISSIONS.TIME_OFF.TOIL_VIEW_OWN);
  // Earlier HR terms, as legacy offered them to admin/HR only.
  const canPickTerm = useHasPermission(HRM_PERMISSIONS.TIME_OFF.LEAVE_VIEW_ALL);
  const { actions, dialogs } = useTimeOffRequestActions();

  const canViewAllocation = canViewLeave && (canViewSheet || canViewOverview);
  const tabs: MyTimeOffTab[] = [
    ...(canViewLeave ? [HISTORY] : []),
    ...(canViewAllocation ? [ALLOCATION] : []),
    ...(canViewToil ? [TOIL] : []),
  ];
  const [tab, setTab] = useUrlTab(tabs, tabs[0] ?? HISTORY, TAB_ALIASES);

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader title={t('MY_TIME_OFF')} actions={actions} />

      <Tabs value={tab} onValueChange={setTab}>
        {/* Full width on a phone, each tab an equal share (scrolls if they don't fit). */}
        <TabsList className="max-sm:w-full max-sm:justify-start max-sm:overflow-x-auto">
          {canViewLeave && (
            <TabsTrigger value={HISTORY} className="max-sm:flex-1">
              {t('LEAVE_HISTORY')}
            </TabsTrigger>
          )}
          {canViewAllocation && (
            <TabsTrigger value={ALLOCATION} className="max-sm:flex-1">
              {t('LEAVE_SHEET')}
            </TabsTrigger>
          )}
          {canViewToil && (
            <TabsTrigger value={TOIL} className="max-sm:flex-1">
              {t('TOIL_REQUESTS')}
            </TabsTrigger>
          )}
        </TabsList>
        {canViewLeave && (
          <TabsContent value={HISTORY}>
            <LeaveHistoryTab />
          </TabsContent>
        )}
        {canViewAllocation && (
          <TabsContent value={ALLOCATION}>
            {/* Component Map › Leave Sheet: one view; the day-by-day ledger needs the sheet permission. */}
            <LeaveSheetView userId={userId} canPickTerm={canPickTerm} showLedger={canViewSheet} />

          </TabsContent>
        )}
        {canViewToil && (
          <TabsContent value={TOIL}>
            <MyToilTab />
          </TabsContent>
        )}
      </Tabs>

      {dialogs}
    </div>
  );
};
